/**
 * PotionSystem - Manages active potion effects on the player.
 *
 * Each effect has a duration (seconds), and the system ticks down
 * timers every frame.  Instant effects (duration=0) are applied
 * once and immediately removed.
 */

// ============================================================
// POTION EFFECT DEFINITIONS
// ============================================================

export const POTION_EFFECTS = {
  // --- Positive ---
  SPEED:           { name: 'Velocidad',        duration: 60, color: '#7CAFC6', speedMultiplier: 1.4 },
  STRENGTH:        { name: 'Fuerza',           duration: 60, color: '#932423', damageBonus: 3 },
  HEALING:         { name: 'Curación',         duration: 0,  color: '#F82423', healAmount: 6 },   // instant
  FIRE_RESISTANCE: { name: 'Resist. Fuego',    duration: 60, color: '#E49A3A', fireImmune: true },
  NIGHT_VISION:    { name: 'Visión Nocturna',  duration: 60, color: '#1F1FA1', nightVision: true },
  REGENERATION:    { name: 'Regeneración',     duration: 30, color: '#CD5CAB', regenPerSec: 0.5 },
  // --- Negative ---
  POISON:          { name: 'Veneno',           duration: 45, color: '#4B8E23', damagePerSec: 0.5, canKill: false },
  WEAKNESS:        { name: 'Debilidad',        duration: 30, color: '#484D48', damageBonus: -4 },
  SLOWNESS:        { name: 'Lentitud',         duration: 30, color: '#5A6C7D', speedMultiplier: 0.5 },
  WITHER_EFFECT:   { name: 'Wither',           duration: 40, color: '#352A27', damagePerSec: 0.5, canKill: true },
  HUNGER_EFFECT:   { name: 'Hambre',           duration: 30, color: '#587653', hungerDrainMult: 2.0 },
  BLINDNESS:       { name: 'Ceguera',          duration: 20, color: '#1F1F23', fogNear: 1, fogFar: 6 },
  NAUSEA:          { name: 'Náusea',           duration: 15, color: '#556B2F', cameraWobble: true },
  // --- Raid effects ---
  BAD_OMEN:        { name: 'Mal Augurio',      duration: 6000, color: '#0B4F2A' },
  HERO_OF_VILLAGE: { name: 'Héroe de la Aldea', duration: 2400, color: '#44DD44', tradeDiscount: 0.3 },
};

// ============================================================
// PotionSystem CLASS
// ============================================================

export class PotionSystem {
  constructor() {
    /** @type {Map<string, { timeLeft: number, level: number }>} */
    this.activeEffects = new Map();
  }

  /**
   * Apply a potion effect.
   * If the effect is instant (duration=0 in POTION_EFFECTS), it is
   * NOT stored in activeEffects -- the caller should handle the
   * instant action (e.g. healing) by checking POTION_EFFECTS directly.
   *
   * @param {string} effectId - Key in POTION_EFFECTS
   * @param {number} [duration] - Override duration (seconds); defaults to effect definition
   * @param {number} [level=1]  - Effect level / potency
   */
  applyEffect(effectId, duration, level = 1) {
    const def = POTION_EFFECTS[effectId];
    if (!def) return;

    const dur = duration !== undefined ? duration : def.duration;

    // Instant effects are not tracked
    if (dur <= 0) return;

    // If effect already active, refresh (take the longer remaining time)
    const existing = this.activeEffects.get(effectId);
    if (existing) {
      existing.timeLeft = Math.max(existing.timeLeft, dur);
      existing.level = Math.max(existing.level, level);
    } else {
      this.activeEffects.set(effectId, { timeLeft: dur, level });
    }
  }

  /**
   * Remove an active effect immediately.
   */
  removeEffect(effectId) {
    this.activeEffects.delete(effectId);
  }

  /**
   * Tick down all active effect timers.  Expired effects are removed.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    for (const [id, data] of this.activeEffects) {
      data.timeLeft -= dt;
      if (data.timeLeft <= 0) {
        this.activeEffects.delete(id);
      }
    }
  }

  /**
   * Check if an effect is currently active.
   */
  hasEffect(effectId) {
    return this.activeEffects.has(effectId);
  }

  /**
   * Get the combined speed multiplier from active effects.
   * Combines Speed (boost) and Slowness (reduction) multiplicatively.
   */
  getSpeedMultiplier() {
    let mult = 1.0;
    if (this.activeEffects.has('SPEED')) {
      mult *= POTION_EFFECTS.SPEED.speedMultiplier || 1.0;
    }
    if (this.activeEffects.has('SLOWNESS')) {
      mult *= POTION_EFFECTS.SLOWNESS.speedMultiplier || 1.0;
    }
    return mult;
  }

  /**
   * Get the combined damage bonus from active effects.
   * Combines Strength (positive) and Weakness (negative).
   */
  getDamageBonus() {
    let bonus = 0;
    if (this.activeEffects.has('STRENGTH')) {
      bonus += POTION_EFFECTS.STRENGTH.damageBonus || 0;
    }
    if (this.activeEffects.has('WEAKNESS')) {
      bonus += POTION_EFFECTS.WEAKNESS.damageBonus || 0;
    }
    return bonus;
  }

  /**
   * Whether the player is immune to fire damage.
   */
  isFireImmune() {
    return this.activeEffects.has('FIRE_RESISTANCE');
  }

  /**
   * Get health regen per second from active effects.
   */
  getRegenPerSec() {
    if (!this.activeEffects.has('REGENERATION')) return 0;
    return POTION_EFFECTS.REGENERATION.regenPerSec || 0;
  }

  /**
   * Whether night vision is active.
   */
  hasNightVision() {
    return this.activeEffects.has('NIGHT_VISION');
  }

  /**
   * Get poison damage per second (does NOT kill — floors at 1 HP).
   */
  getPoisonDamage() {
    if (!this.activeEffects.has('POISON')) return 0;
    return POTION_EFFECTS.POISON.damagePerSec || 0;
  }

  /**
   * Get wither damage per second (CAN kill).
   */
  getWitherDamage() {
    if (!this.activeEffects.has('WITHER_EFFECT')) return 0;
    return POTION_EFFECTS.WITHER_EFFECT.damagePerSec || 0;
  }

  /**
   * Get hunger drain multiplier (1.0 = normal, 2.0 = double drain).
   */
  getHungerDrainMultiplier() {
    if (!this.activeEffects.has('HUNGER_EFFECT')) return 1.0;
    return POTION_EFFECTS.HUNGER_EFFECT.hungerDrainMult || 1.0;
  }

  /**
   * Whether blindness is active (extreme fog).
   */
  isBlind() {
    return this.activeEffects.has('BLINDNESS');
  }

  /**
   * Get blindness fog distances.
   */
  getBlindnessFog() {
    if (!this.activeEffects.has('BLINDNESS')) return null;
    return { near: POTION_EFFECTS.BLINDNESS.fogNear, far: POTION_EFFECTS.BLINDNESS.fogFar };
  }

  /**
   * Whether nausea (camera wobble) is active.
   */
  hasNausea() {
    return this.activeEffects.has('NAUSEA');
  }

  /**
   * Get trade discount multiplier (0.0-1.0). 0 = no discount.
   */
  getTradeDiscount() {
    if (!this.activeEffects.has('HERO_OF_VILLAGE')) return 0;
    return POTION_EFFECTS.HERO_OF_VILLAGE.tradeDiscount || 0;
  }

  /**
   * Get a snapshot of all active effects for the HUD.
   * @returns {Array<{ id: string, name: string, timeLeft: number, color: string }>}
   */
  getActiveEffects() {
    const result = [];
    for (const [id, data] of this.activeEffects) {
      const def = POTION_EFFECTS[id];
      if (!def) continue;
      result.push({
        id,
        name: def.name,
        timeLeft: data.timeLeft,
        color: def.color,
      });
    }
    return result;
  }
}
